import { useEffect, useState } from 'react'
import { graphql } from 'app/generated/gql'
import { gqlQuery } from 'app/lib/graphqlClient'
import { bg } from 'lib/background'
import { logWarn } from 'lib/logging'

const STAFF_EMAIL_DOMAIN = `trackable.io`

// By id, not the `users` list: the list hides org and super users whenever the
// caller sits in a workspace, and staff are usually exactly those users.
const OVERRIDE_USER_QUERY = graphql(`
  query FeatureToggleOverrideUser($id: ID!) {
    user(id: $id) {
      id
      email
    }
  }
`)

const isCustomerById = new Map<string, Promise<boolean>>()

function lookUpIsCustomer(id: string): Promise<boolean> {
  const cached = isCustomerById.get(id)
  if (cached) return cached
  const pending = (async () => {
    try {
      const { data, error } = await gqlQuery(OVERRIDE_USER_QUERY, { id })
      // A failed lookup is not an answer: forget it so the next pass asks again.
      if (error) isCustomerById.delete(id)
      const email = data?.user?.email
      return !error && !!email && !email.includes(STAFF_EMAIL_DOMAIN)
    } catch (err) {
      logWarn(`user(${id}) email lookup threw`, err)
      isCustomerById.delete(id)
      return false
    }
  })()
  isCustomerById.set(id, pending)
  return pending
}

// Ids whose email is known to be a customer's. Anyone still loading, failed, or
// on Trackable staff is left out, so they can never flag a toggle as behind.
export function useCustomerUserIds(userIds: readonly string[]): ReadonlySet<string> {
  const [customerIds, setCustomerIds] = useState<ReadonlySet<string>>(() => new Set())
  const idsKey = [...new Set(userIds)].sort().join(`,`)

  useEffect(() => {
    let cancelled = false
    const ids = idsKey ? idsKey.split(`,`) : []
    bg(async () => {
      const results = await Promise.all(ids.map(lookUpIsCustomer))
      if (!cancelled) setCustomerIds(new Set(ids.filter((_, i) => results[i])))
    })
    return () => {
      cancelled = true
    }
  }, [idsKey])

  return customerIds
}
